/**
 * Le videochiamate: le chiamate al server, in un posto solo.
 *
 * Il contratto è `packages/shared/src/videochiamate.ts` — tipi e rotte — e questo file lo
 * segue alla lettera senza ripeterne la forma: ogni funzione qui sotto è una rotta di là, con
 * il nome in italiano e il tipo di ritorno preso dal pacchetto condiviso. Il server lo scrive
 * un altro, in parallelo; il Cruscotto programma contro il contratto, ed è il contratto che li
 * tiene d'accordo (§116: un fatto, un padrone).
 *
 * ## Tre modi di entrare, e perché passano tutti di qui
 *
 * 1. **Il personale**, con la sessione del Cruscotto: il cookie `httpOnly` viaggia da solo
 *    dentro `api`, e un 401 è una sessione da rinnovare come su ogni altra pagina.
 * 2. **L'account di sala**, con un gettone `type: 'sala'` che il server emette su
 *    `/sala/accesso` e che **non è** una sessione del Cruscotto: non ha un cookie, non si
 *    rinnova, e la guardia globale lo rifiuta su ogni altra rotta. Viaggia in un'intestazione
 *    `Authorization: Bearer` e vive in `localStorage` sotto una chiave sua, separato dalla
 *    sessione del personale che sullo stesso browser potrebbe esserci o no. Un 401 col
 *    gettone di sala **non** deve mandare nessuno all'ingresso del Cruscotto: tutte le
 *    chiamate di sala passano con `noRefresh`, che è esattamente la porta che `http.ts` offre
 *    per «qui il 401 è la risposta, non un incidente».
 * 3. **L'ospite esterno**, con il segreto nel link e nessuna sessione: le tre rotte pubbliche
 *    passano anche loro con `noRefresh`, per la stessa ragione della risposta a un invito.
 *
 * Le rotte di gestione (`/:codice/attesa`, `/partecipanti/...`, `/termina`) sono le stesse per
 * il personale e per la sala: cambia solo **chi** le chiama, e lo decide `Credenziali`.
 */

import type {
  AccessoSala,
  AccountSalaVista,
  AttesaVista,
  ChiamataInElenco,
  ChiamataVista,
  CodiceInstallatore,
  Disponibilita,
  ImpostaAccountSala,
  IngressoChiamata,
  InvitoOspite,
  NuovaChiamata,
  NuovoOspite,
  OspiteVista,
  SalaVista,
  ScegliPostazione,
  SessioneSala,
  SquilloVista,
  StatoAttesa,
} from '@bestehr/shared';
import { FORMA_APPARECCHIO } from '@bestehr/shared';

import { api, type RequestOptions } from './http';

/* ------------------------------------------------------------ chi sta chiamando */

/**
 * Con quale titolo si parla al server.
 *
 * `personale` è il cookie del Cruscotto: non c'è niente da allegare. `sala` è il gettone
 * dell'account di sala, allegato a mano e senza rinnovo.
 */
/** Una mia presenza aperta in una chiamata (`GET /videochiamate/io/altrove`). */
export interface PresenzaAltrove {
  codice: string;
  titolo: string;
  /** `telefono` o `pc`; `null` per gli ingressi di prima del 2026-10-28. */
  dispositivo: string | null;
  dal: string;
}

export type Credenziali = { tipo: 'personale' } | { tipo: 'sala'; gettone: string };

export const PERSONALE: Credenziali = { tipo: 'personale' };

function opzioni(cred: Credenziali, extra: RequestOptions = {}): RequestOptions {
  if (cred.tipo === 'personale') return extra;
  return {
    ...extra,
    noRefresh: true,
    headers: { ...extra.headers, authorization: `Bearer ${cred.gettone}` },
  };
}

/* ---------------------------------------------------------------- la sessione di sala */

/**
 * La sessione dell'account di sede su questo PC («Beste Sale»).
 *
 * ## Una sessione per PC, condivisa fra le finestre — e il perché
 *
 * L'account è **uno per sede** (HUB, COLLE, CILIANI) e Cosimo vuole il **multilogin**: lo stesso
 * account su più PC e in più finestre dello stesso PC insieme, ognuna nella sua chiamata. Ogni
 * accesso (`POST /sala/accesso`) dà una **sessione nuova** — l'identità nel motore è
 * `s:<account>.<sessione>` — quindi due PC non si pestano mai. Sullo stesso PC, invece, le
 * finestre **condividono** la sessione (è in `localStorage`): l'alternativa — un accesso per
 * finestra — vorrebbe o la parola d'ordine ribattuta a ogni finestra, o la parola salvata sul PC,
 * e un segreto in chiaro su un PC di saletta non si scrive. Condividere costa una sola cosa: due
 * finestre dello stesso PC **nella stessa chiamata** avrebbero la stessa identità e il motore
 * terrebbe l'ultima, buttando fuori la prima. Quel caso lo previene `coordinaFinestre` qui sotto:
 * prima di entrare, una finestra chiede alle altre «chi è già in questa chiamata?» su un
 * `BroadcastChannel`, e se una risponde si offre di portarla in primo piano invece di entrare
 * due volte. Chiamate **diverse** in finestre diverse — che è il multilogin che serve — non hanno
 * niente da coordinare: l'identità è unica dentro ogni stanza.
 *
 * `localStorage` e non `sessionStorage` anche per un'altra ragione: il PC di una saletta si
 * riavvia, e la console deve **restare collegata** (fino a `scadeIl`). La sala non si ricorda piu'
 * sul PC: dal 2026-10-27 e' dell'account (uno per sala).
 */
const CHIAVE_SALA = 'bestehr.sala';

export function leggiSessioneSala(): SessioneSala | null {
  try {
    const grezzo = localStorage.getItem(CHIAVE_SALA);
    if (grezzo === null) return null;
    const s = JSON.parse(grezzo) as SessioneSala;
    if (typeof s?.gettone !== 'string' || typeof s?.scadeIl !== 'string' || typeof s?.sala?.id !== 'number') return null;
    // Scaduta: si butta subito, così nessuno la usa per un giro di 401.
    if (new Date(s.scadeIl).getTime() <= Date.now()) {
      localStorage.removeItem(CHIAVE_SALA);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function scriviSessioneSala(s: SessioneSala | null): void {
  try {
    if (s === null) localStorage.removeItem(CHIAVE_SALA);
    else localStorage.setItem(CHIAVE_SALA, JSON.stringify(s));
  } catch {
    // Finestra privata o archivio pieno: la console funziona finché la scheda resta aperta.
  }
}

/** Le credenziali di sala, se una sessione c'è: è quel che la stanza chiede quando arriva da `/sala`. */
export function credenzialiSala(): Credenziali | null {
  const s = leggiSessioneSala();
  return s === null ? null : { tipo: 'sala', gettone: s.gettone };
}

/* ------------------------------------------------------ le finestre dello stesso PC */

const CANALE_FINESTRE = 'bestehr.sala.finestre';

type MessaggioFinestre =
  | { tipo: 'chi'; codice: string; da: string }
  | { tipo: 'dentro'; codice: string; da: string }
  | { tipo: 'vieni'; codice: string; a: string };

export interface CoordinamentoFinestre {
  /** Dichiara che questa finestra è in questa chiamata (o in nessuna, con `null`). */
  dichiara(codice: string | null): void;
  /** Vero se un'altra finestra di questo PC è già in questa chiamata. */
  giaAperta(codice: string): Promise<boolean>;
  /** Chiede alla finestra che è in quella chiamata di mettersi in primo piano. */
  portaInPrimoPiano(codice: string): void;
  chiudi(): void;
}

/**
 * Le finestre di Beste Sale sullo stesso PC si parlano su un `BroadcastChannel`: ognuna dice in
 * quale chiamata sta, e prima di entrare una chiede se un'altra ci è già. Il perché sta in testa
 * alla sessione. Senza `BroadcastChannel` (browser vecchi) si entra e basta.
 */
export function coordinaFinestre(): CoordinamentoFinestre {
  const io = Math.random().toString(36).slice(2, 10);
  let mia: string | null = null;
  let canale: BroadcastChannel | null = null;
  try {
    canale = new BroadcastChannel(CANALE_FINESTRE);
  } catch {
    canale = null;
  }
  const ascoltatori = new Set<(m: MessaggioFinestre) => void>();
  canale?.addEventListener('message', (e: MessageEvent<MessaggioFinestre>) => {
    const m = e.data;
    if (m.tipo === 'chi' && mia === m.codice && m.da !== io) {
      canale?.postMessage({ tipo: 'dentro', codice: m.codice, da: io });
    }
    if (m.tipo === 'vieni' && m.a === io) window.focus();
    for (const f of ascoltatori) f(m);
  });
  return {
    dichiara(codice) {
      mia = codice;
    },
    giaAperta(codice) {
      if (!canale) return Promise.resolve(false);
      return new Promise((ok) => {
        const f = (m: MessaggioFinestre): void => {
          if (m.tipo === 'dentro' && m.codice === codice) {
            ascoltatori.delete(f);
            ok(true);
          }
        };
        ascoltatori.add(f);
        canale?.postMessage({ tipo: 'chi', codice, da: io });
        window.setTimeout(() => {
          ascoltatori.delete(f);
          ok(false);
        }, 350);
      });
    },
    portaInPrimoPiano(codice) {
      const f = (m: MessaggioFinestre): void => {
        if (m.tipo === 'dentro' && m.codice === codice) {
          ascoltatori.delete(f);
          canale?.postMessage({ tipo: 'vieni', codice, a: m.da });
        }
      };
      ascoltatori.add(f);
      canale?.postMessage({ tipo: 'chi', codice, da: io });
    },
    chiudi() {
      mia = null;
      canale?.close();
      canale = null;
    },
  };
}

/* ------------------------------------------------------------------------ le rotte */

export const videochiamate = {
  /* personale */

  /**
   * Il server video c'è? `false` vuol dire «non ci sono», non «errore»: oggi in produzione il
   * motore non c'è ancora, e il Cruscotto esce prima di lui. Chi legge `false` nasconde la voce
   * e i tasti; chi apre una stanza lo stesso trova uno stato scritto, non un guasto.
   */
  disponibili: (): Promise<Disponibilita> => api.get<Disponibilita>('/videochiamate/disponibili'),

  /** Dove sta il motore video, per la prova di rete dell'anteprima: pubblica, anche per ospiti e sale. */
  motore: (): Promise<{ indirizzo: string | null }> =>
    api.get<{ indirizzo: string | null }>('/videochiamate/motore', { noRefresh: true }),

  /** Le mie: in corso, programmate, finite. */
  elenco: (): Promise<ChiamataInElenco[]> => api.get<ChiamataInElenco[]>('/videochiamate'),

  crea: (dati: NuovaChiamata): Promise<ChiamataVista> =>
    api.post<ChiamataVista>('/videochiamate', dati),

  /**
   * La chiamata della riunione del calendario: la crea, o restituisce quella che c'è. `riservata`
   * vale alla nascita: le sale non la vedono, tranne quella prenotata per la riunione.
   */
  daRiunione: (eventId: number, riservata = false): Promise<ChiamataVista> =>
    api.post<ChiamataVista>(`/videochiamate/da-riunione/${eventId}`, { riservata }),

  aggiungiOspite: (codice: string, ospite: NuovoOspite): Promise<OspiteVista> =>
    api.post<OspiteVista>(`/videochiamate/${codice}/ospiti`, ospite),

  revocaOspite: (codice: string, ospiteId: number): Promise<void> =>
    api.delete(`/videochiamate/${codice}/ospiti/${ospiteId}`),

  /**
   * Dove sono collegato adesso (2026-10-28): le mie presenze aperte, scritte dagli eventi del
   * motore, col dispositivo. La shell ci legge «sei in videochiamata dal telefono: continua qui».
   */
  altrove: (signal?: AbortSignal): Promise<PresenzaAltrove[]> =>
    api.get<PresenzaAltrove[]>('/videochiamate/io/altrove', { signal }),

  /* la chiamata diretta che squilla (2026-10-28) */

  /** Chiama un collega: il suo telefono squilla, e torna lo squillo da seguire. */
  chiama: (userId: number): Promise<SquilloVista> => api.post<SquilloVista>('/videochiamate/chiama', { userId }),
  /** La chiamata che squilla per me adesso; `null` se non ce n'e'. */
  inArrivo: (signal?: AbortSignal): Promise<SquilloVista | null> =>
    api.get<SquilloVista | null>('/videochiamate/squilli/in-arrivo', { signal }),
  squillo: (id: number, signal?: AbortSignal): Promise<SquilloVista> =>
    api.get<SquilloVista>(`/videochiamate/squilli/${id}`, { signal }),
  rispondi: (id: number): Promise<SquilloVista> => api.post<SquilloVista>(`/videochiamate/squilli/${id}/rispondi`, {}),
  rifiuta: (id: number): Promise<SquilloVista> => api.post<SquilloVista>(`/videochiamate/squilli/${id}/rifiuta`, {}),
  annulla: (id: number): Promise<SquilloVista> => api.post<SquilloVista>(`/videochiamate/squilli/${id}/annulla`, {}),

  /** «Continua sul telefono»: una notifica a me, il cui tocco fa entrare subito dal telefono. */
  continuaSulTelefono: (codice: string): Promise<void> =>
    api.post(`/videochiamate/${codice}/continua-sul-telefono`, {}),

  /* personale e sala */

  dettaglio: (codice: string, cred: Credenziali = PERSONALE): Promise<ChiamataVista> =>
    api.get<ChiamataVista>(`/videochiamate/${codice}`, opzioni(cred)),

  /** Il gettone LiveKit: è l'unica cosa che apre la stanza. */
  ingresso: (codice: string, cred: Credenziali = PERSONALE): Promise<IngressoChiamata> =>
    api.post<IngressoChiamata>(`/videochiamate/${codice}/ingresso`, {}, opzioni(cred)),

  /* gestione (moderatore) */

  attesa: (codice: string, cred: Credenziali, signal?: AbortSignal): Promise<AttesaVista[]> =>
    api.get<AttesaVista[]>(`/videochiamate/${codice}/attesa`, opzioni(cred, { signal })),

  ammetti: (codice: string, attesaId: number, cred: Credenziali): Promise<void> =>
    api.post(`/videochiamate/${codice}/attesa/${attesaId}/ammetti`, {}, opzioni(cred)),

  respingi: (codice: string, attesaId: number, cred: Credenziali): Promise<void> =>
    api.post(`/videochiamate/${codice}/attesa/${attesaId}/respingi`, {}, opzioni(cred)),

  silenzia: (
    codice: string,
    identita: string,
    traccia: 'audio' | 'video',
    cred: Credenziali,
  ): Promise<void> =>
    api.post(
      `/videochiamate/${codice}/partecipanti/${identita}/silenzia`,
      { traccia },
      opzioni(cred),
    ),

  allontana: (codice: string, identita: string, cred: Credenziali): Promise<void> =>
    api.post(`/videochiamate/${codice}/partecipanti/${identita}/allontana`, {}, opzioni(cred)),

  promuovi: (codice: string, identita: string, cred: Credenziali): Promise<void> =>
    api.post(`/videochiamate/${codice}/partecipanti/${identita}/promuovi`, {}, opzioni(cred)),

  riammetti: (codice: string, identita: string, cred: Credenziali): Promise<void> =>
    api.post(`/videochiamate/${codice}/partecipanti/${identita}/riammetti`, {}, opzioni(cred)),

  chiudiIngressi: (codice: string, chiusi: boolean, cred: Credenziali): Promise<void> =>
    api.post(`/videochiamate/${codice}/chiudi-ingressi`, { chiusi }, opzioni(cred)),

  termina: (codice: string, cred: Credenziali): Promise<void> =>
    api.post(`/videochiamate/${codice}/termina`, {}, opzioni(cred)),

  /* account di sala */

  accessoSala: (dati: AccessoSala): Promise<SessioneSala> =>
    api.post<SessioneSala>('/videochiamate/sala/accesso', dati, { noRefresh: true }),

  /** La sala dove sta questo PC (o nessuna): torna un gettone **nuovo**, con la postazione dentro. */
  scegliPostazione: (dati: ScegliPostazione, cred: Credenziali): Promise<SessioneSala> =>
    api.post<SessioneSala>('/videochiamate/sala/postazione', dati, opzioni(cred)),

  /** Lo scaricamento dell'app delle sale: un codice monouso che la fa entrare con questo account. */
  installatoreSala: (cred: Credenziali): Promise<CodiceInstallatore> =>
    api.post<CodiceInstallatore>('/videochiamate/sala/installatore', {}, opzioni(cred)),

  /** Il primo avvio dell'app delle sale: il codice diventa una sessione. */
  abbinaSala: (codice: string): Promise<SessioneSala> =>
    api.post<SessioneSala>('/videochiamate/sala/abbina', { codice }, { noRefresh: true }),

  /** Il login della sala non scade: stessa sessione, scadenza nuova. */
  rinnovaSala: (cred: Credenziali): Promise<SessioneSala> =>
    api.post<SessioneSala>('/videochiamate/sala/rinnova', {}, opzioni(cred)),

  salaMe: (cred: Credenziali): Promise<SalaVista> =>
    api.get<SalaVista>('/videochiamate/sala/me', opzioni(cred)),

  /** Le chiamate di oggi, tutte tranne le riservate. */
  salaChiamate: (cred: Credenziali, signal?: AbortSignal): Promise<ChiamataInElenco[]> =>
    api.get<ChiamataInElenco[]>('/videochiamate/sala/chiamate', opzioni(cred, { signal })),

  /* ospite esterno (pubbliche) */

  invitoOspite: (segreto: string): Promise<InvitoOspite> =>
    api.get<InvitoOspite>(`/videochiamate/ospite/${segreto}`, { noRefresh: true }),

  /*
    L'apparecchio (2026-11-03): ogni scheda che apre un link ospite bussa ed entra per conto suo, cosi' lo
    stesso link aperto da due posti non scaccia nessuno (`apparecchioOspite`).
  */
  chiediDiEntrare: (segreto: string, nome: string, apparecchio: string): Promise<StatoAttesa> =>
    api.post<StatoAttesa>(`/videochiamate/ospite/${segreto}/attesa`, { nome, apparecchio }, { noRefresh: true }),

  /** «Annulla» in sala d'attesa: la richiesta esce subito dall'elenco di chi modera. */
  ritiraAttesa: (segreto: string, apparecchio: string): Promise<void> =>
    api.delete(`/videochiamate/ospite/${segreto}/attesa?apparecchio=${apparecchio}`, { noRefresh: true }),

  statoAttesa: (segreto: string, apparecchio: string, signal?: AbortSignal): Promise<StatoAttesa> =>
    api.get<StatoAttesa>(`/videochiamate/ospite/${segreto}/attesa?apparecchio=${apparecchio}`, { noRefresh: true, signal }),

  /** Un link nuovo da condividere con gli ospiti (chi modera: organizzatore, promosso, saletta). */
  linkOspiti: (codice: string, cred: Credenziali, lingua?: 'it' | 'en' | 'zh'): Promise<{ link: string; ospite: OspiteVista }> =>
    api.post<{ link: string; ospite: OspiteVista }>(`/videochiamate/${codice}/link-ospiti`, lingua ? { lingua } : {}, opzioni(cred)),
};

/**
 * L'apparecchio di questa scheda per un link ospite: 8 caratteri `[a-z0-9]` (`FORMA_APPARECCHIO`), scelti a
 * caso la prima volta e tenuti nella **scheda** (`sessionStorage`), cosi' ricaricare la pagina rientra
 * come lo stesso apparecchio (e prende il posto del proprio fantasma) mentre un'altra scheda o un altro
 * computer sono apparecchi diversi. Senza memoria (finestra privata che la rifiuta) vale per la pagina.
 */
const apparecchiInMemoria = new Map<string, string>();
export function apparecchioOspite(segreto: string): string {
  const chiave = `bestehr.ospite.apparecchio.${segreto.slice(0, 12)}`;
  try {
    const gia = sessionStorage.getItem(chiave);
    if (gia && FORMA_APPARECCHIO.test(gia)) return gia;
  } catch {
    /* scheda senza memoria: si ripiega sotto */
  }
  const inMemoria = apparecchiInMemoria.get(chiave);
  if (inMemoria) return inMemoria;
  const alfabeto = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const byte = crypto.getRandomValues(new Uint8Array(8));
  const nuovo = [...byte].map((b) => alfabeto[b % alfabeto.length]).join('');
  apparecchiInMemoria.set(chiave, nuovo);
  try {
    sessionStorage.setItem(chiave, nuovo);
  } catch {
    /* vale per questa pagina */
  }
  return nuovo;
}

/* ------------------------------------------------------------ gli account delle sale */

/**
 * L'account di sala, **uno per sede**, dalla gestione delle sale: tre rotte sotto
 * `/headquarters/:id`, con le forme del contratto (`AccountSalaVista`, `ImpostaAccountSala`). La
 * `GET` risponde sempre una vista: senza account ha `id: null`. La `DELETE` lo **spegne**, non lo
 * cancella: la sede resta, la credenziale smette di aprire.
 */
export const accountSala = {
  /** Una riga per sala attiva (uno per sala dal 2026-10-27), col suo account se c'e'. */
  elenco: (): Promise<AccountSalaVista[]> => api.get<AccountSalaVista[]>('/account-sala'),

  imposta: (salaId: number, dati: ImpostaAccountSala): Promise<AccountSalaVista> =>
    api.put<AccountSalaVista>(`/account-sala/${salaId}`, dati),

  spegni: (salaId: number): Promise<AccountSalaVista> => api.delete<AccountSalaVista>(`/account-sala/${salaId}`),
};

/* ----------------------------------------------------------------- piccole regole */

/**
 * Il testo finisce negli appunti, con il ripiego per i browser che non espongono
 * `navigator.clipboard` fuori da un'origine sicura (un PC di saletta su `http://` interno).
 */
export async function copiaNegliAppunti(testo: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(testo);
      return true;
    }
  } catch {
    /* si passa al ripiego */
  }
  try {
    const area = document.createElement('textarea');
    area.value = testo;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const riuscito = document.execCommand('copy');
    area.remove();
    return riuscito;
  } catch {
    return false;
  }
}
